/**
 * Typed client for the Premise Worker API (SPEC §8).
 *
 * - Base URL from VITE_API_BASE, default http://localhost:8787 (wrangler dev).
 * - Every request sends `credentials: 'include'` so the signed HttpOnly session
 *   cookie rides along cross-origin (Pages → Worker).
 * - The Worker wraps every response in one envelope: { ok, data } | { ok, error }.
 *   `request()` unwraps `data` and throws `ApiError` on failure so callers get a
 *   plain value or a typed throw.
 *
 * NOTE ON CONTRACTS: the owner/tenant route modules are built by sibling agents
 * in parallel, so the exact JSON of composite endpoints (overview, ledger,
 * my/tenancy) is not yet frozen. The response types below are the front-end's
 * best-effort shapes derived from the schema + SPEC; the screens read them
 * defensively (optional chaining, fallbacks) and never hard-fail if a field or
 * the whole API is missing. Adjust these types to match once the API lands.
 */

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ??
  'http://localhost:8787';

/* ------------------------------------------------------------------ */
/*  Envelope + error                                                   */
/* ------------------------------------------------------------------ */
type Envelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { message: string; code?: string } };

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** True when the failure means "not signed in" — callers route to Login. */
export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  });

  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    // Non-JSON (e.g. a raw CSV or an HTML error page).
    if (!res.ok) throw new ApiError(res.status, res.statusText || 'Request failed');
    // Shouldn't happen for JSON endpoints; treat as empty.
    return undefined as T;
  }

  if (!body.ok) {
    throw new ApiError(res.status, body.error?.message ?? 'Request failed', body.error?.code);
  }
  return body.data;
}

/* ------------------------------------------------------------------ */
/*  Domain types (mirror src/db/schema.ts row shapes; web is standalone) */
/* ------------------------------------------------------------------ */
export type Role = 'owner' | 'tenant' | 'staff';
export type LedgerStatus = 'due' | 'paid' | 'partial' | 'overdue' | 'waived';
export type GstMode = 'none' | 'add' | 'rcm';
export type RequestCategory = 'plumbing' | 'electrical' | 'ac' | 'structure' | 'other';
export type RequestStatus = 'submitted' | 'seen' | 'assigned' | 'in_progress' | 'done';

export interface Me {
  id: string;
  partyId: string;
  role: Role;
  displayName?: string;
  email?: string | null;
  phone?: string | null;
}

/** Raw shape the API returns for /me and /auth/otp/verify: { user, party? }. */
interface AuthUserResponse {
  user: { id: string; partyId: string; role: Role };
  party?: { displayName?: string; email?: string | null; phone?: string | null } | null;
}

/** Flatten the server's { user, party } into the app's flat Me. */
function normalizeMe(d: AuthUserResponse): Me {
  return {
    id: d.user.id,
    partyId: d.user.partyId,
    role: d.user.role,
    displayName: d.party?.displayName,
    email: d.party?.email ?? null,
    phone: d.party?.phone ?? null,
  };
}

export interface Property {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  unitCount?: number;
}

export interface Tenancy {
  id: string;
  unitId: string;
  tenantPartyId: string;
  status: string;
  startDate?: number | null;
  endDate?: number | null;
  lockInEnd?: number | null;
  noticePeriodDays?: number;
  rentAmount: number;
  rentDueDay?: number;
  depositAmount: number;
  depositStatus?: string;
  tdsEnabled: boolean;
  tdsRateBps: number;
  gstMode: GstMode;
  gstRateBps: number;
  escalationEnabled: boolean;
  escalationPctBps?: number | null;
  escalationCycleMonths?: number | null;
  escalationNextDate?: number | null;
  camEnabled: boolean;
  electricityEnabled: boolean;
}

/** A tenancy as it appears in the roster / overview — enriched with join data. */
export interface TenancyRow {
  id: string;
  /** true = a removable demo row (Example badge), never a real tenancy. */
  isSample?: boolean;
  unitLabel: string;
  unitKind?: string;
  floor?: string | null;
  tenantName: string;
  tenantPhone?: string | null;
  rentAmount: number;
  dueDay?: number;
  /** tenancy lifecycle: 'active' | 'upcoming' | 'ended' — only active gets nudged. */
  lifecycle?: string;
  /** current-month ledger status, if the API resolves it. */
  status?: LedgerStatus;
  netExpected?: number;
  netReceived?: number;
  balance?: number;
  dueDate?: number;
  dueInDays?: number | null;
}

/** One tenant's payment reliability (GET /collections). */
export interface CollectionRow {
  tenancyId: string;
  tenantName: string;
  tenantPhone?: string | null;
  unitLabel: string;
  rentAmount: number;
  dueDate?: number;
  dueInDays?: number | null;
  onTimePct: number;
  avgDaysLate: number;
  monthsTotal: number;
  monthsOnTime: number;
  currentOverdueDays: number;
  outstanding: number;
  /** Periods with an actual payment outcome, and periods still unpaid. */
  monthsSettled: number;
  openPeriods: number;
  /** 'new' = not enough settled history to judge yet, NOT a severity band. */
  risk: 'new' | 'low' | 'watch' | 'high';
  history: Array<{ period: string; status: 'onTime' | 'late' | 'unpaid' }>;
}

/** One gap on the Overview's "Finish setting up" card. */
export interface SetupItem {
  key: string;
  label: string;
  why: string;
  done: boolean;
  /** The tenancy to act on, when the gap is fixable on a specific record. */
  tenancyId: string | null;
  /** Which sheet fixes it: open the tenancy, or its invite / edit / payment sheet. */
  action: 'open' | 'invite' | 'edit' | 'payment';
}

export interface SetupStatus {
  items: SetupItem[];
  done: number;
  total: number;
  complete: boolean;
}

/** Portfolio arrears, aged by how many days past due (paise). */
export interface ArrearsAgeing {
  total: number;
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b90: number;
}

/** One tenant's rent landing in the selected month. */
export interface CashCalendarItem {
  tenancyId: string;
  tenantName: string;
  day: number;
  amount: number;
  received: number;
  status: 'received' | 'partial' | 'due';
}

/** A computed ledger row (mirrors lib/money PeriodComputation + receipt state). */
export interface LedgerRow {
  period: string; // "YYYY-MM"
  dueDate?: number | null;
  baseRent: number;
  escalationAmount: number;
  rent: number;
  camAmount: number;
  electricityAmount: number;
  gstAmount: number;
  gstModeSnapshot: GstMode;
  grossAmount: number;
  tdsAmount: number;
  netExpected: number;
  received: number;
  balance: number;
  status: LedgerStatus;
}

export interface TenancyDetail {
  tenancy: Tenancy;
  unitLabel: string;
  unitKind?: string;
  floor?: string | null;
  tenantName: string;
  propertyName?: string;
}

/** A payment as the register lists it — a dated credit against the balance. */
export interface RegisterPayment {
  id: string;
  amount: number; // paise
  paidOn?: number | null;
  method?: string;
  reference?: string | null;
  period: string; // "YYYY-MM" the payment is allocated to
}

/** The FY chip + scope metadata a ledger response carries for the filter row. */
export interface LedgerWindow {
  scope: 'fy' | 'all';
  label: string; // "FY 26–27" | "All time"
  selectedFy: number | null;
  start: string; // "YYYY-MM"
  end: string; // "YYYY-MM"
  fyOptions: Array<{ fy: number; label: string }>;
}

/** How the caller scopes a ledger/statement fetch. Omit → current FY (default). */
export type LedgerScope = { fy?: number; all?: boolean };

export interface TenancyLedger {
  tenancy?: Tenancy;
  detail?: TenancyDetail;
  periods: LedgerRow[];
  payments: RegisterPayment[];
  openingBalance: number;
  closingBalance: number;
  balanceOutstanding: number;
  window?: LedgerWindow;
  /** days the earliest unpaid period is overdue (>0), or null. */
  overdueDays?: number | null;
}

export interface CriticalDateRow {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  dueDate?: number | null;
  dueInDays?: number | null;
  amount?: number | null;
  amountLabel?: string;
  overdue?: boolean;
}

export interface Overview {
  property: Property;
  stats: {
    occupied: number;
    units: number;
    expected: number; // paise
    collected: number; // paise
    overdue: number; // paise
    overdueCount: number;
  };
  dates: CriticalDateRow[];
  tenancies: TenancyRow[];
  arrears?: ArrearsAgeing;
  cashCalendar?: CashCalendarItem[];
  setup?: SetupStatus;
  period?: string;
}

/** Tenant home (GET /my/tenancy). */
export interface MyTenancy {
  tenancyId: string;
  tenantName: string;
  unitLabel: string;
  propertyName: string;
  period: string;
  netExpected: number;
  rent: number;
  tdsAmount: number;
  gstMode: GstMode;
  dueDate?: number | null;
  dueInDays?: number | null;
  status: LedgerStatus;
  depositAmount: number;
  next?: { period: string; dueDate?: number | null; netExpected: number } | null;
}

export interface MyLedger {
  periods: LedgerRow[];
  payments: RegisterPayment[];
  openingBalance: number;
  closingBalance: number;
  balanceOutstanding: number;
  depositAmount: number;
  fyLabel?: string;
  window?: LedgerWindow;
}

export interface DocumentRow {
  id: string;
  kind: string;
  title: string;
  mime?: string | null;
  size?: number | null;
  createdAt?: number;
  expiryDate?: number | null;
  sharedWithTenant?: boolean;
  /** True when the tenant uploaded this file themselves (vs shared by the owner). */
  mine?: boolean;
  subtitle?: string;
}

export interface RequestRow {
  id: string;
  category: RequestCategory;
  description?: string | null;
  status: RequestStatus;
  createdAt?: number;
  updatedAt?: number;
  events?: Array<{ status: RequestStatus; note?: string | null; createdAt?: number }>;
}

/* ------------------------------------------------------------------ */
/*  Owner write-path types (parties, units, tenancies, dates, requests, docs) */
/*  These mirror the REAL row shapes the Worker returns (verified by curl). */
/* ------------------------------------------------------------------ */

/** A tenant business (`party` row). GST is often N/A; TDS lives on the tenancy. */
export interface TenantParty {
  id: string;
  kind: 'owner' | 'tenant';
  displayName: string;
  legalName?: string | null;
  gstin?: string | null;
  pan?: string | null;
  phone?: string | null;
  email?: string | null;
  createdAt?: number;
}

/** Body for POST /parties. `kind` is forced to 'tenant' server-side. */
export interface CreateTenantPayload {
  displayName: string;
  entityType?: 'individual' | 'business';
  legalName?: string;
  gstin?: string;
  pan?: string;
  email?: string;
  phone?: string;
}

/**
 * Body for PATCH /parties/:id — every field optional. Setting email/phone is what
 * lets the tenant OTP-log-in (the "invite tenant" action). null clears a field.
 */
export interface UpdateTenantPayload {
  displayName?: string;
  legalName?: string | null;
  gstin?: string | null;
  pan?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** A `unit` row (GET /units, POST /units). */
export interface Unit {
  id: string;
  propertyId: string;
  label: string;
  floor?: string | null;
  roomNumber?: string | null;
  kind: string;
  areaSqft?: number | null;
  baseRent?: number | null;
  deposit?: number | null;
  status?: 'available' | 'out_of_service';
  /** true = a removable "Load sample data" demo row, shown with an Example badge. */
  isSample?: boolean;
  createdAt?: number;
}

/** Whether the removable demo set is currently loaded (GET /sample-data). */
export interface SampleStatus {
  present: boolean;
  tenancies: number;
  units: number;
  parties: number;
}

/** A room on the Rooms board (GET /properties/:id/rooms) — unit + live occupancy. */
export interface RoomRow extends Unit {
  occupied: boolean;
  tenancyId?: string | null;
  tenantName?: string | null;
  rentAmount?: number | null;
  payStatus?: LedgerStatus | null;
  dueInDays?: number | null;
}

/** Body for POST /units. propertyId + label required; kind defaults to 'shop'. */
export interface CreateUnitPayload {
  propertyId: string;
  label: string;
  floor?: string;
  roomNumber?: string;
  kind?: string;
  areaSqft?: number;
  baseRent?: number;
  deposit?: number;
  status?: 'available' | 'out_of_service';
}

/** Body for PATCH /units/:id — any subset; null clears a nullable field. */
export interface UpdateUnitPayload {
  label?: string;
  floor?: string | null;
  roomNumber?: string | null;
  kind?: string;
  areaSqft?: number | null;
  baseRent?: number | null;
  deposit?: number | null;
  status?: 'available' | 'out_of_service';
}

/**
 * Body for POST /tenancies. unitId + tenantPartyId are required; ownerPartyId is
 * forced to the caller server-side. Everything else falls back to schema defaults
 * (a "simple" tenancy = all toggles off). Money is integer paise; dates epoch ms.
 */
export interface CreateTenancyPayload {
  unitId: string;
  tenantPartyId: string;
  status?: string;
  startDate?: number | null;
  endDate?: number | null;
  lockInEnd?: number | null;
  noticePeriodDays?: number;
  rentAmount?: number;
  rentDueDay?: number;
  depositAmount?: number;
  depositStatus?: string;
  tdsEnabled?: boolean;
  tdsRateBps?: number;
  gstMode?: GstMode;
  gstRateBps?: number;
  escalationEnabled?: boolean;
  escalationPctBps?: number | null;
  escalationCycleMonths?: number | null;
  escalationNextDate?: number | null;
  camEnabled?: boolean;
  camBasis?: 'fixed' | 'per_sqft' | null;
  camAmount?: number | null;
  electricityEnabled?: boolean;
}

/** Body for PATCH /tenancies/:id — any subset of the tenancy terms + toggles. */
export type UpdateTenancyPayload = Partial<CreateTenancyPayload>;

/** Body for POST /tenancies/:id/payments. amount (positive paise) is required. */
export interface RecordPaymentPayload {
  amount: number;
  method?: 'neft' | 'upi' | 'cheque' | 'cash' | 'other';
  reference?: string;
  /** "YYYY-MM" — links the payment to that month's ledger row (created if needed). */
  ledger_period?: string;
  /** epoch ms; defaults to now server-side. */
  paid_on?: number;
}

/** A `payment` row (POST /tenancies/:id/payments response). */
export interface Payment {
  id: string;
  tenancyId: string;
  ledgerPeriodId?: string | null;
  amount: number;
  method: string;
  reference?: string | null;
  paidOn?: number | null;
  recordedBy?: string | null;
  createdAt?: number;
}

/** A full `critical_date` row (GET/POST/PATCH /critical-dates). */
export interface CriticalDateRecord {
  id: string;
  propertyId?: string | null;
  tenancyId?: string | null;
  type: string;
  title: string;
  anchor: 'fixed' | 'relative_to_end' | 'relative_to_start';
  offsetDays?: number | null;
  dueDate?: number | null;
  leadDays?: string | null;
  status: 'upcoming' | 'done' | 'dismissed';
  assignee?: string | null;
  note?: string | null;
  createdAt?: number;
}

/** Body for POST /critical-dates. type + title required; one of propertyId/tenancyId required. */
export interface CreateCriticalDatePayload {
  type: string;
  title: string;
  propertyId?: string;
  tenancyId?: string;
  anchor?: 'fixed' | 'relative_to_end' | 'relative_to_start';
  offsetDays?: number | null;
  dueDate?: number | null;
  leadDays?: string;
  status?: 'upcoming' | 'done' | 'dismissed';
  assignee?: string;
  note?: string;
}

/** Body for PATCH /critical-dates/:id — any subset; null clears a nullable field. */
export interface UpdateCriticalDatePayload {
  type?: string;
  title?: string;
  anchor?: 'fixed' | 'relative_to_end' | 'relative_to_start';
  offsetDays?: number | null;
  dueDate?: number | null;
  leadDays?: string | null;
  status?: 'upcoming' | 'done' | 'dismissed';
  assignee?: string | null;
  note?: string | null;
}

/** A full `request` row (GET /requests list, PATCH /requests/:id response). */
export interface RequestRecord {
  id: string;
  tenancyId: string;
  category: RequestCategory;
  description?: string | null;
  status: RequestStatus;
  createdBy?: string | null;
  /** Role of the raiser ('owner' | 'tenant') — for the By-tenant / By-owner stamp. */
  createdByRole?: string | null;
  assignee?: string | null;
  createdAt?: number;
  updatedAt?: number;
}

/** Body for PATCH /requests/:id — status only advances; assignee/note optional. */
export interface AdvanceRequestPayload {
  status?: RequestStatus;
  assignee?: string;
  note?: string;
}

/** A `request_event` row (POST /requests/:id/events response). */
export interface RequestEventRecord {
  id: string;
  requestId: string;
  status: RequestStatus;
  note?: string | null;
  actor?: string | null;
  actorRole?: string | null;
  mediaKeys?: string | null;
  internal?: boolean;
  createdAt?: number;
}

/** Body for POST /requests/:id/events — note and/or media_keys (csv or array). */
export interface AddRequestEventPayload {
  note?: string;
  media_keys?: string | string[];
  /** Owner-only: keep this note private (hidden from the tenant). */
  internal?: boolean;
}

/** A full `document` row (GET /documents list, POST /documents response). */
export interface DocumentRecord {
  id: string;
  propertyId?: string | null;
  tenancyId?: string | null;
  kind: string;
  title: string;
  r2Key?: string;
  mime?: string | null;
  size?: number | null;
  expiryDate?: number | null;
  sharedWithTenant?: boolean;
  uploadedBy?: string | null;
  /** Role of the uploader ('owner' | 'tenant' | 'staff'), for the "by tenant" badge. */
  uploadedByRole?: string | null;
  createdAt?: number;
}

/** Query filter for GET /documents (owner: any owned scope). */
export interface DocumentsQuery {
  tenancyId?: string;
  propertyId?: string;
}

/* ------------------------------------------------------------------ */
/*  Raw server shapes + mappers for composite endpoints                */
/*  The route modules return their own envelopes; these adapt them to  */
/*  the flat view-model types above.                                   */
/* ------------------------------------------------------------------ */

/** GET /properties/:id/overview — server returns tiles + dateRadar + roster. */
interface OverviewResponse {
  property: Property;
  tiles: {
    units: number;
    occupied: number;
    vacant: number;
    rentExpected: number;
    collected: number;
    overdueCount: number;
    overdueAmount: number;
    period: string;
  };
  dateRadar: Array<
    CriticalDateRow & { note?: string | null }
  >;
  tenancies: TenancyRow[];
  arrears?: ArrearsAgeing;
  cashCalendar?: CashCalendarItem[];
  setup?: SetupStatus;
}

function mapOverview(d: OverviewResponse): Overview {
  return {
    property: d.property,
    stats: {
      units: d.tiles.units,
      occupied: d.tiles.occupied,
      expected: d.tiles.rentExpected,
      collected: d.tiles.collected,
      overdue: d.tiles.overdueAmount,
      overdueCount: d.tiles.overdueCount,
    },
    dates: d.dateRadar ?? [],
    tenancies: d.tenancies ?? [],
    arrears: d.arrears,
    cashCalendar: d.cashCalendar,
    setup: d.setup,
    period: d.tiles.period,
  };
}

/** GET /tenancies/:id/ledger — server nests the balance under `summary`. */
interface LedgerResponse {
  tenancyId: string;
  periods: LedgerRow[];
  payments?: RegisterPayment[];
  window?: LedgerWindow;
  summary: {
    outstanding: number;
    openingBalance?: number;
    closingBalance?: number;
    deposit: number;
    depositStatus: string;
    financialYear: { startPeriod: string; endPeriod: string; startEpoch: number };
  };
}

function mapLedger(d: LedgerResponse): TenancyLedger {
  // Days the earliest still-overdue period has been overdue (>0), else null.
  const now = Date.now();
  let overdueDays: number | null = null;
  for (const p of d.periods) {
    if (p.status === 'overdue' && p.balance > 0 && p.dueDate != null) {
      const days = Math.floor((now - p.dueDate) / 86_400_000);
      if (days > 0 && (overdueDays == null || days > overdueDays)) overdueDays = days;
    }
  }
  return {
    periods: d.periods,
    payments: d.payments ?? [],
    openingBalance: d.summary?.openingBalance ?? 0,
    closingBalance: d.summary?.closingBalance ?? d.summary?.outstanding ?? 0,
    balanceOutstanding: d.summary?.outstanding ?? 0,
    window: d.window,
    overdueDays,
  };
}

/** Build the query string for a ledger/statement scope (current FY when empty). */
function scopeQuery(scope?: LedgerScope): string {
  if (scope?.all) return '?scope=all';
  if (scope?.fy) return `?fy=${scope.fy}`;
  return '';
}

/** GET /my/tenancy — server returns one enriched element per tenancy. */
interface MyTenancyElement {
  tenancy: Tenancy;
  tenantName: string;
  unit: { label: string } | null;
  property: { name: string } | null;
  deposit: { amount: number; status: string };
  currentMonth: {
    period: string;
    rent: number;
    tdsAmount: number;
    gstModeSnapshot: GstMode;
    netExpected: number;
  };
  status: LedgerStatus;
  dueDate?: number | null;
  dueInDays?: number | null;
  next?: { period: string; dueDate?: number | null; netExpected: number } | null;
}

function mapMyTenancy(list: MyTenancyElement[]): MyTenancy {
  const el = list.find((e) => e.tenancy.status === 'active') ?? list[0];
  if (!el) throw new ApiError(404, 'No tenancy for this account');
  const cm = el.currentMonth;
  return {
    tenancyId: el.tenancy.id,
    tenantName: el.tenantName,
    unitLabel: el.unit?.label ?? '–',
    propertyName: el.property?.name ?? '',
    period: cm.period,
    netExpected: cm.netExpected,
    rent: cm.rent,
    tdsAmount: cm.tdsAmount,
    gstMode: cm.gstModeSnapshot,
    dueDate: el.dueDate ?? null,
    dueInDays: el.dueInDays ?? null,
    status: el.status,
    depositAmount: el.deposit.amount,
    next: el.next ?? null,
  };
}

/** GET /my/ledger — server returns `months` with a nested `breakdown`. */
interface MyLedgerResponse {
  tenancyId: string;
  fy: { start: string; end: string };
  window?: LedgerWindow;
  deposit: { amount: number; status: string };
  months: Array<{
    period: string;
    dueDate: number | null;
    netExpected: number;
    received: number;
    balance: number;
    status: LedgerStatus;
    breakdown: Omit<LedgerRow, 'dueDate' | 'received' | 'balance' | 'status'>;
  }>;
  payments?: RegisterPayment[];
  openingBalance?: number;
  closingBalance?: number;
  /** Authoritative outstanding — only periods already due. */
  balanceOutstanding?: number;
}

function fyLabelFrom(fy?: { start: string; end: string }): string | undefined {
  if (!fy) return undefined;
  const sy = fy.start.slice(0, 4);
  const ey = fy.end.slice(2, 4);
  return `FY ${sy}–${ey}`;
}

function mapMyLedger(d: MyLedgerResponse): MyLedger {
  const periods: LedgerRow[] = d.months.map((m) => ({
    ...m.breakdown,
    period: m.period,
    dueDate: m.dueDate,
    netExpected: m.netExpected,
    received: m.received,
    balance: m.balance,
    status: m.status,
  }));
  // Prefer the server's due-to-date figure; fall back to summing only periods
  // that are actually due (overdue/partial) so future months never inflate it.
  const balanceOutstanding =
    d.balanceOutstanding ??
    d.months
      .filter((m) => m.status === 'overdue' || m.status === 'partial')
      .reduce((sum, m) => sum + Math.max(0, m.balance), 0);
  return {
    periods,
    payments: d.payments ?? [],
    openingBalance: d.openingBalance ?? 0,
    closingBalance: d.closingBalance ?? balanceOutstanding,
    balanceOutstanding,
    depositAmount: d.deposit?.amount ?? 0,
    fyLabel: d.window?.label ?? fyLabelFrom(d.fy),
    window: d.window,
  };
}

/* ------------------------------------------------------------------ */
/*  The client                                                         */
/* ------------------------------------------------------------------ */
export const api = {
  /* --- auth --- */
  requestOtp: (identifier: string) =>
    request<{ sent: boolean; devCode?: string }>('/auth/otp/request', {
      method: 'POST',
      json: { identifier },
    }),

  verifyOtp: async (identifier: string, code: string): Promise<Me> => {
    const d = await request<AuthUserResponse>('/auth/otp/verify', {
      method: 'POST',
      json: { identifier, code },
    });
    return normalizeMe(d);
  },

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  me: async (): Promise<Me> => normalizeMe(await request<AuthUserResponse>('/me')),

  /** Update the signed-in user's own profile (name / email / phone). */
  updateProfile: async (patch: { displayName?: string; email?: string | null; phone?: string | null }): Promise<Me> =>
    normalizeMe(await request<AuthUserResponse>('/me', { method: 'PATCH', json: patch })),

  /* --- owner --- */
  properties: () => request<Property[]>('/properties'),

  property: (id: string) => request<Property>(`/properties/${id}`),

  /** Update a property's name / address / location (fenced to the owner). */
  updateProperty: (id: string, patch: { name?: string; address?: string | null; city?: string | null; state?: string | null }) =>
    request<Property>(`/properties/${id}`, { method: 'PATCH', json: patch }),

  /** Property overview for a month (`period` = "YYYY-MM"); defaults to current. */
  overview: async (propertyId: string, period?: string): Promise<Overview> =>
    mapOverview(
      await request<OverviewResponse>(
        `/properties/${propertyId}/overview${period ? `?period=${period}` : ''}`,
      ),
    ),

  tenancies: () => request<TenancyRow[]>('/tenancies'),

  tenancy: (id: string) => request<TenancyDetail>(`/tenancies/${id}`),

  /** Tenancy ledger for a scope (current FY when omitted). */
  ledger: async (tenancyId: string, scope?: LedgerScope): Promise<TenancyLedger> =>
    mapLedger(await request<LedgerResponse>(`/tenancies/${tenancyId}/ledger${scopeQuery(scope)}`)),

  /** PATCH toggles / terms on a tenancy (full patch — any subset of terms + toggles). */
  updateTenancy: (id: string, patch: UpdateTenancyPayload) =>
    request<Tenancy>(`/tenancies/${id}`, { method: 'PATCH', json: patch }),

  sendReminder: (tenancyId: string) =>
    request<{ sent: boolean }>('/reminders/send', {
      method: 'POST',
      json: { tenancyId },
    }),

  /** Absolute URL to the CA export (CSV) for a scope — open/download in a new tab. */
  caExportUrl: (tenancyId: string, scope?: LedgerScope) =>
    `${API_BASE}/export/ca/${tenancyId}${scopeQuery(scope)}`,

  /* --- tenants (parties) --- */
  /** Add a new tenant business. kind is forced to 'tenant' server-side. */
  createTenant: (payload: CreateTenantPayload) =>
    request<TenantParty>('/parties', { method: 'POST', json: payload }),

  /** Edit a tenant party. Setting email/phone is the "invite tenant" action. */
  updateTenant: (id: string, patch: UpdateTenantPayload) =>
    request<TenantParty>(`/parties/${id}`, { method: 'PATCH', json: patch }),

  /** Read one tenant party (fenced to the caller's tenancies). */
  getParty: (id: string) => request<TenantParty>(`/parties/${id}`),

  /* --- units --- */
  /** List all units under the owner's properties (optional propertyId filter). */
  units: (propertyId?: string) =>
    request<Unit[]>(`/units${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`),

  createUnit: (payload: CreateUnitPayload) =>
    request<Unit>('/units', { method: 'POST', json: payload }),

  updateUnit: (id: string, patch: UpdateUnitPayload) =>
    request<Unit>(`/units/${id}`, { method: 'PATCH', json: patch }),

  /** The Rooms board — every unit with live occupancy, current tenant + pay-status. */
  rooms: (propertyId: string) => request<RoomRow[]>(`/properties/${propertyId}/rooms`),

  /* --- tenancies (write path) --- */
  createTenancy: (payload: CreateTenancyPayload) =>
    request<Tenancy>('/tenancies', { method: 'POST', json: payload }),

  /** Record a payment against a tenancy (optionally linked to a "YYYY-MM" period). */
  recordPayment: (tenancyId: string, payload: RecordPaymentPayload) =>
    request<Payment>(`/tenancies/${tenancyId}/payments`, { method: 'POST', json: payload }),

  /* --- critical dates --- */
  /** List the owner's critical dates (optional propertyId / tenancyId filter). */
  criticalDates: (params?: { propertyId?: string; tenancyId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.propertyId) qs.set('propertyId', params.propertyId);
    if (params?.tenancyId) qs.set('tenancyId', params.tenancyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<CriticalDateRecord[]>(`/critical-dates${suffix}`);
  },

  createCriticalDate: (payload: CreateCriticalDatePayload) =>
    request<CriticalDateRecord>('/critical-dates', { method: 'POST', json: payload }),

  updateCriticalDate: (id: string, patch: UpdateCriticalDatePayload) =>
    request<CriticalDateRecord>(`/critical-dates/${id}`, { method: 'PATCH', json: patch }),

  /** (Re)generate a tenancy's lease-derived key dates. Returns count + all its dates. */
  deriveTenancyDates: (tenancyId: string) =>
    request<{ created: number; dates: CriticalDateRecord[] }>(`/tenancies/${tenancyId}/derive-dates`, { method: 'POST' }),

  /* --- requests (owner side) --- */
  /** List maintenance requests under the owner's property. */
  /** Per-tenant payment reliability, ranked worst-first. */
  /* --- sample data (removable demo set) --- */
  sampleStatus: () => request<SampleStatus>('/sample-data'),
  loadSampleData: () => request<{ loaded: boolean }>('/sample-data', { method: 'POST' }),
  removeSampleData: () => request<{ removed: boolean }>('/sample-data', { method: 'DELETE' }),

  collections: () => request<CollectionRow[]>('/collections'),

  ownerRequests: () => request<RequestRecord[]>('/requests'),

  /** Owner logs an issue against a tenancy (origin-stamped as owner). */
  logRequest: (payload: { tenancyId: string; category: RequestCategory; description?: string }) =>
    request<RequestRecord>('/requests', { method: 'POST', json: payload }),

  /** Advance a request's status (+ optional assignee/note). Status only advances. */
  advanceRequest: (id: string, patch: AdvanceRequestPayload) =>
    request<RequestRecord>(`/requests/${id}`, { method: 'PATCH', json: patch }),

  /** Append an event (note and/or media keys) to a request's timeline. */
  addRequestEvent: (id: string, payload: AddRequestEventPayload) =>
    request<RequestEventRecord>(`/requests/${id}/events`, { method: 'POST', json: payload }),

  /* --- documents --- */
  /** List documents (owner: any owned scope; optional tenancyId / propertyId filter). */
  documents: (params?: DocumentsQuery) => {
    const qs = new URLSearchParams();
    if (params?.tenancyId) qs.set('tenancyId', params.tenancyId);
    if (params?.propertyId) qs.set('propertyId', params.propertyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<DocumentRecord[]>(`/documents${suffix}`);
  },

  /**
   * Upload a document (multipart/form-data). Build the FormData with a `file`
   * field plus text fields tenancyId?/propertyId?, kind, title, expiry_date?,
   * shared_with_tenant?. No content-type header — the browser sets the boundary.
   */
  uploadDocument: (formData: FormData) =>
    request<DocumentRecord>('/documents', { method: 'POST', body: formData }),

  /** Absolute URL to stream/download a document's bytes (fenced GET). */
  documentContentUrl: (id: string) => `${API_BASE}/documents/${id}/content`,

  /* --- tenant (fenced server-side to the caller's tenancy) --- */
  myTenancy: async (): Promise<MyTenancy> =>
    mapMyTenancy(await request<MyTenancyElement[]>('/my/tenancy')),

  myLedger: async (scope?: LedgerScope): Promise<MyLedger> =>
    mapMyLedger(await request<MyLedgerResponse>(`/my/ledger${scopeQuery(scope)}`)),

  myDocuments: () => request<DocumentRow[]>('/my/documents'),

  myRequests: () => request<RequestRow[]>('/my/requests'),

  createRequest: (payload: { category: RequestCategory; description: string }) =>
    request<RequestRow>('/my/requests', { method: 'POST', json: payload }),

  markPaid: (period: string) =>
    request<{ ok: true }>('/my/payments/mark-paid', {
      method: 'POST',
      json: { period },
    }),
};

export type Api = typeof api;
